'use client';

import { handleIncomeSubmit } from 'actions/Forms';
import { useActionState, useEffect, useState } from 'react';
import { ComponentFields } from 'types/content';
import FormError from './FormError';
import { saveSecondInterview } from 'actions/Database';
import { useAppDispatch, useAppSelector } from 'lib/hooks';
import { selectDynamicValues } from 'slices/dynamicSlice';
import { selectUser, setUserDatabase } from 'slices/userSlice';
import { getFilingStatus } from 'lib/eligibilityFormulas';
import { ANALYTICS_EVENTS, capture } from 'lib/analytics';

const IncomeForm = ({ data, onContinue }: ComponentFields) => {
  const dispatch = useAppDispatch();
  const dynamicValues = useAppSelector(selectDynamicValues);
  const user = useAppSelector(selectUser);
  const [income, setIncome] = useState<string>('');
  const [filingStatus, setFilingStatus] = useState<string>('');

  const [state, formAction, isPending] = useActionState(handleIncomeSubmit, {
    data: new FormData(),
    errors: {},
    results: {},
    success: false,
  });

  useEffect(() => {
    if (state.success) {
      const filingStatus = getFilingStatus(
        user.currently_married === true,
        (state.results as { filed: string }).filed
      );

      saveSecondInterview(
        (state.results as { income: string }).income,
        filingStatus,
        dynamicValues['usesHearingAids'] === 'yes',
        dynamicValues
      )
        .then((user) => {
          dispatch(
            setUserDatabase({ ...user, created_at: null, updated_at: null })
          );
        })
        .finally(() => {
          capture(ANALYTICS_EVENTS.incomeFormCompleted);
          onContinue();
        });
    }
  }, [state.success]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <section className="form-container">
      <form action={formAction}>
        <fieldset>
          <div className="form-header">
            <p className="lead-in-text">
              Your Part B and Part D premiums can scale up, based on your income
              from two years ago.
            </p>
            <legend>{data.elements.title?.value}</legend>
            <p className="explanation-text">(Just make your best guess.)</p>
          </div>

          <div className="row">
            <div className="checkbox-container">
              <label className="checkbox-label">
                <input
                  id="filing-status-individual"
                  type="radio"
                  name="filed"
                  value="individually"
                  defaultChecked={state.data.get('filed') === 'individually'}
                  aria-describedby={
                    state.errors.filed ? 'filing-status-error' : undefined
                  }
                  onChange={(e) => setFilingStatus(e.target.value)}
                />
                <span className="checkbox"></span>
                <span data-testid="radio-filing-individually">
                  Filed individually
                </span>
              </label>
              <label className="checkbox-label">
                <input
                  id="filing-status-jointly"
                  type="radio"
                  name="filed"
                  value="jointly"
                  data-testid="radio-filing-jointly"
                  defaultChecked={state.data.get('filed') === 'jointly'}
                  aria-describedby={
                    state.errors.filed ? 'filing-status-error' : undefined
                  }
                  onChange={(e) => setFilingStatus(e.target.value)}
                />
                <span className="checkbox"></span>
                <span>Filed jointly</span>
              </label>
            </div>
          </div>

          <div className="row currency-input">
            <span className="dollar-sign">$</span>
            <input
              id="income"
              type="text"
              name="income"
              data-testid="input-income"
              className="half"
              placeholder=""
              defaultValue={state.data.get('income') as string}
              required
              aria-invalid={Boolean(state.errors.income)}
              aria-describedby={
                state.errors.income ? 'income-error' : undefined
              }
              value={income}
              onChange={(e) => setIncome(e.target.value)}
            />
          </div>
          {state.errors.income && (
            <FormError error={state.errors.income} id="income-error" />
          )}

          {state.errors.filed && (
            <FormError error={state.errors.filed} id="filing-status-error" />
          )}

          <button
            className="button primary submit"
            type="submit"
            data-testid="submit-income"
            disabled={filingStatus === '' || income === ''}
          >
            Submit
          </button>

          {isPending && <div className="spinner"></div>}
        </fieldset>
      </form>
    </section>
  );
};

export default IncomeForm;
